// src/components/lists/AccountsList/AccountsList.tsx

import React, { useMemo } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ListContainer } from '../base/ListStyles';
import { styled } from '@mui/material/styles';
import { colors } from '../../../theme/theme';

export type Column<T> = {
  field?: keyof T;
  label: string;
  width?: number;
  mobileWidth?: number;
  flex?: number;
  render?: (item: T) => React.ReactNode;
};

type AccountsListProps<T extends { id: number }> = {
  items: T[];
  columns: Column<T>[];
};

const cellBase = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: '.5rem .5rem',
  overflow: 'hidden',
  minWidth: 0,
  boxSizing: 'border-box' as const,
};

const GridRow = styled(Box)({
  display: 'grid',
  width: '100%',
});

const HeaderRow = styled(GridRow)(({ theme }) => ({
  backgroundColor: colors.tertiary.main,
  borderBottom: `1px solid ${theme.palette.primary.dark}`,
  '& > *': {
    ...cellBase,
    color: colors.grey[0],
    '& p': {
      fontWeight: 700,
      letterSpacing: '1.5px',
      fontSize: '1.25rem',
    },
  },
}));

const DataRow = styled(GridRow, {
  shouldForwardProp: (prop) => prop !== 'isEven',
})<{ isEven?: boolean }>(({ theme, isEven }) => ({
  backgroundColor: isEven
    ? theme.palette.mode === 'dark'
      ? theme.palette.grey[800]
      : colors.grey[50]
    : theme.palette.mode === 'dark'
      ? theme.palette.grey[700]
      : theme.palette.grey[300],
  borderBottom: `1px solid ${theme.palette.grey[500]}`,
  minHeight: '5rem',
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? colors.grey[300]
        : theme.palette.tertiary.light,
  },
  '&:focus-within': {
    outline: `3px solid ${colors.accessibility.focusColor}`,
    outlineOffset: '-3px',
  },
  '& > *': {
    ...cellBase,
    color: theme.palette.mode === 'dark' ? colors.grey[900] : colors.grey[900],
    borderRight: `1px solid ${theme.palette.grey[500]}`,
    fontWeight: 700,
    fontSize: '1.75rem',
  },
}));

export const BaseRowItem = styled(Box)({});
export const DateCell = styled(Box)({ fontVariantNumeric: 'tabular-nums' });

export const ChipCell = styled(Box)({
  '&&': {
    justifyContent: 'center',
  },
  '& .MuiChip-root': {
    padding: '1.5rem 0.25rem',
  },
  '& .MuiChip-label': {
    fontSize: '1.5rem',
  },
});

export const TextCell = styled(Box)({
  '&&': {
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  width: '100%',
  '& > span': {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
    alignSelf: 'flex-start',
  },
});

export const CurrencyCell = styled(Box)({
  fontVariantNumeric: 'tabular-nums',
});

export const LinkCell = styled(Box)({
  '& button': {
    fontSize: 'inherit',
    fontWeight: 'inherit',
    textTransform: 'none',
    padding: 0,
    minWidth: 0,
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
    color: 'inherit',
    cursor: 'pointer',
    textDecoration: 'none',
    transform: 'none',
    transition: 'none',
    '&:hover, &:focus, &:active, &:focus-visible': {
      background: 'transparent',
      boxShadow: 'none',
      textDecoration: 'none',
      transform: 'none',
      color: 'inherit',
    },
  },
});

export const ActionCell = styled(Box)({
  gap: '0.25rem',
  flexWrap: 'nowrap',
  '& .MuiButton-root': {
    fontSize: '1.1rem',
    padding: '0.2rem 0.5rem',
    minWidth: 0,
    whiteSpace: 'nowrap',
  },
  '& .MuiButton-startIcon': {
    marginRight: '0.2rem',
    '& svg': { fontSize: '1.2rem' },
  },
});

const ScrollWrapper = styled(Box)({
  overflowX: 'auto',
  overflowY: 'visible',
  WebkitOverflowScrolling: 'touch',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
});

function buildGridTemplate<T>(columns: Column<T>[], isMobile: boolean): string {
  return columns
    .map((col) => {
      const w =
        isMobile && col.mobileWidth != null ? col.mobileWidth : col.width;
      if (w && col.flex) return `minmax(${w}px, ${col.flex}fr)`;
      if (w) return `${w}px`;
      if (col.flex) return `${col.flex}fr`;
      return '120px';
    })
    .join(' ');
}

function calcMinWidth<T>(columns: Column<T>[], isMobile: boolean): number {
  return columns.reduce((sum, col) => {
    const w = isMobile && col.mobileWidth != null ? col.mobileWidth : col.width;
    return sum + (w ?? 120);
  }, 0);
}

function AccountsList<T extends { id: number }>({
  items,
  columns,
}: AccountsListProps<T>) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const gridTemplateColumns = useMemo(
    () => buildGridTemplate(columns, isMobile),
    [columns, isMobile]
  );
  const minWidth = useMemo(
    () => calcMinWidth(columns, isMobile),
    [columns, isMobile]
  );

  return (
    <ListContainer>
      <ScrollWrapper>
        <Box sx={{ minWidth }}>
          <HeaderRow sx={{ gridTemplateColumns }}>
            {columns.map((col, idx) => (
              <Box key={idx}>
                <Typography>{col.label}</Typography>
              </Box>
            ))}
          </HeaderRow>

          {items.map((item, rowIdx) => (
            <DataRow
              key={item.id}
              isEven={rowIdx % 2 === 0}
              sx={{ gridTemplateColumns }}
            >
              {columns.map((col, colIdx) =>
                col.render ? (
                  <React.Fragment key={colIdx}>
                    {col.render(item)}
                  </React.Fragment>
                ) : (
                  <Box key={colIdx}>
                    {col.field ? String(item[col.field]) : null}
                  </Box>
                )
              )}
            </DataRow>
          ))}
        </Box>
      </ScrollWrapper>
    </ListContainer>
  );
}

export default AccountsList;
